import Phaser from 'phaser';

/**
 * Global event bus for decoupled communication between game systems.
 * All game events flow through this singleton emitter.
 */
export const EventBus = new Phaser.Events.EventEmitter();

// Event name constants
export const GameEvents = {
  ENEMY_SPAWNED: 'enemySpawned',
  ENEMY_REACHED_BASE: 'enemyReachedBase',
  ENEMY_DESTROYED: 'enemyDestroyed',
  WAVE_STARTED: 'waveStarted',
  WAVE_COMPLETED: 'waveCompleted',
  BASE_DAMAGED: 'baseDamaged',
  BASE_NODE_DESTROYED: 'baseNodeDestroyed',
  BASE_DESTROYED: 'baseDestroyed',
  GAME_OVER: 'gameOver',
  ROBOT_PLACED: 'robotPlaced',
  ROBOT_DESTROYED: 'robotDestroyed',
  ROBOT_FIRED: 'robotFired',
  ROBOT_EXPLODED: 'robotExploded',
  ROBOT_SELECTED: 'robotSelected',
  PHASE_CHANGED: 'phaseChanged',
  RESOURCES_CHANGED: 'resourcesChanged',
  ENEMY_FIRED: 'enemyFired',
  KILL_STREAK: 'killStreak',
  ENEMY_BASE_DAMAGED: 'enemyBaseDamaged',
  ENEMY_BASE_DESTROYED: 'enemyBaseDestroyed',
  ALL_ENEMY_BASES_DESTROYED: 'allEnemyBasesDestroyed',
  BASE_UPGRADED: 'baseUpgraded',
  BASE_CLICKED: 'baseClicked',
  CRITICAL_HIT: 'criticalHit',
  MINE_PLACED: 'minePlaced',
  MINE_TRIGGERED: 'mineTriggered',
  MINE_EXPLODED: 'mineExploded',
  GOD_PARAMS_CHANGED: 'godParamsChanged',
  TUTORIAL_PRESET_SELECTED: 'tutorialPresetSelected',
  TILE_REPAIRED: 'tileRepaired',
  ROBOT_REPAIRED: 'robotRepaired',
  ARMOR_SHIELD_DEPLETED: 'armorShieldDepleted',
  ARMOR_RICOCHET: 'armorRicochet',
  LANGUAGE_CHANGED: 'languageChanged',

  // Projectile events
  PROJECTILE_HIT: 'projectileHit',
  PROJECTILE_MISS: 'projectileMiss',
  MISSILE_EXPLODED: 'missileExploded',
  LASER_FIRED: 'laserFired',
  MUD_TILE_CONVERTED: 'mudTileConverted',

  // Robot combat/state events
  WEAPON_OVERHEAT: 'weaponOverheat',
  COMPONENT_BROKEN: 'componentBroken',
  ROBOT_SURVIVED_HIT: 'robotSurvivedHit',
  ROBOT_TOPPLED: 'robotToppled',
  ROBOT_CRASHING: 'robotCrashing',

  // Behavior events
  PURSUE_ACTIVATED: 'pursueActivated',
  KAMIKAZE_WAR_CRY: 'kamikazeWarCry',
  JUMPER_PUSH: 'jumperPush',
  DECOR_HARVESTED: 'decorHarvested',
  DECOR_MINING: 'decorMining',         // excavator dealing damage to resource (for HP bar)

  // Enemy events
  ENEMY_DAMAGE_DEALT: 'enemyDamageDealt',
  WEAPON_COMBO: 'weaponCombo',
  TERRAIN_DEATH: 'terrainDeath',
  MINE_KILLS: 'mineKills',

  // Progression events
  CURRENCY_CHANGED: 'currencyChanged',
  LEVEL_UNLOCKED: 'levelUnlocked',
  LEVEL_COMPLETED: 'levelCompleted',
  COMPONENT_UNLOCKED: 'componentUnlocked',
  COMPONENT_EVOLVED: 'componentEvolved',
  GRID_EXPANDED: 'gridExpanded',
  BASE_UPGRADE_CHANGED: 'baseUpgradeChanged',
  KILLS_UPDATED: 'killsUpdated',

  // Wave/phase events
  WAVE_CELEBRATION: 'waveCelebration',
  WAVE_TRANSITION_READY: 'waveTransitionReady',
  WAVE_CHAINED: 'waveChained',
  CPU_REQUEST_WAVE: 'cpuRequestWave',

  // Workshop events
  WORKSHOP_DESIGN_COMPLETE: 'workshopDesignComplete',
  WORKSHOP_EDIT_COMPLETE: 'workshopEditComplete',

  // Save/settings events
  SETTINGS_CHANGED: 'settingsChanged',
  SCORE_SAVED: 'scoreSaved',
  RESOURCES_UPDATED: 'resourcesUpdated',
  CUSTOM_MAPS_CHANGED: 'customMapsChanged',

  // Achievement events
  ACHIEVEMENT_UNLOCKED: 'achievementUnlocked',
  REWARD_CLAIMED: 'rewardClaimed',

  // Easter egg events
  EASTER_EGG_CRT_GLITCH: 'easterEgg:crtGlitch',
  EASTER_EGG_FLOATING_WARNING: 'easterEgg:floatingWarning',

  // Billiard knockback events
  BILLIARD_KNOCKBACK_START: 'billiardKnockbackStart',
  BILLIARD_WALL_BOUNCE: 'billiardWallBounce',
  BILLIARD_ENEMY_COLLISION: 'billiardEnemyCollision',
  BILLIARD_KNOCKBACK_END: 'billiardKnockbackEnd',

  // Fun tracking events
  ENEMY_STUNNED: 'enemyStunned',

  // Hive death-spawn event
  HIVE_DEATH_SPAWN: 'hiveDeathSpawn',

  // Healer pulse event
  HEALER_PULSE: 'healerPulse',

  // Loot fly-to-HUD events
  COIN_FLY_TO_HUD: 'coinFlyToHud',
  COIN_ARRIVED: 'coinArrived',
  RESOURCE_FLY_TO_HUD: 'resourceFlyToHud',
  RESOURCE_ARRIVED: 'resourceArrived',
} as const;
